// Returns the nth line of a message as delimited by \r, \n, or \r\n
// Does so in place without using string.split() to save on memory

import { StringDecoder } from '../gflib/src/lib/types';
import { parseHeaders } from '../gflib/src/lib/mime-headers/parseHeaders';
import { parseHeaderValueAndParams } from '../gflib/src/lib/mime-headers/parseHeaderValueAndParams';
import { ParsedHeaderValue } from '../gflib/src/lib/mime-headers/parseHeaderValueAndParams.types';
import { decodeWords } from '../gflib/src/lib/mime-enc/decode-words';
import { getUnixMs } from '../gflib/src/lib/email/date-time';
import { decodeString, encodeString } from '../gflib/src/lib/str-enc/native';
import { decode, encode } from 'iconv-lite';
import { MboxData } from '../stores/appStateStore';
import { MimeNode, MultipartMimeNode } from '../gflib/src/lib/mime-tree/types';

// in case of very large message
export function findNthLine(message: string, n: number) {
    let currLine = 0;
    let i = 0;
    while(i < message.length && currLine < n) {
        if(message.charAt(i) === '\n') {
            currLine++;
        }
        if(message.charAt(i) === '\r') {
            currLine++;
            if(i+1 < message.length && message.charAt(i+1) === '\n') {
                i++;
            }
        }
        i++;
    }
    if(i >= message.length) {
        return '';
    }
    let res = '';
    while(i < message.length) {
        if(message.charAt(i) === '\n' || message.charAt(i) === '\r') {
            break;
        }
        res += message.charAt(i);
        i++;
    }

    return res;
} // findNthLine()

export function generatePrettyPath(path: string, capitalizationMap?: Map<string, string>) {
    const pathParts = path.split('/');
    if(pathParts.length === 1 || (pathParts.length === 2 && pathParts[1] === '')) {
        return pathParts[0];
    }
    let builder = pathParts[0];
    for(let i = 1; i < pathParts.length; i++) {
        builder += ' > ' + (capitalizationMap&&capitalizationMap.get(pathParts[i]));
    }
    return builder;
} // generatePrettyPath()

export interface GmailHeaders {
    subject?: string;
    from?: string;
    to?: string;
    labels?: string[];
    date?: number; // unix
    mimeVersion?: string;
    contentType?: string;
    warning: boolean;
}

// Adapted from getSubjectAndContType() of mbox.ts from gflib
export function parseGmailHeaders(
	// eslint-disable-next-line @typescript-eslint/no-shadow
	decodeString: StringDecoder,
	emailData: Buffer | string
): GmailHeaders {
	const { result: parsed } = parseHeaders(emailData, {
		badLinesSkip: false,
	});

	let subject: string | undefined;
    let from: string | undefined;
    let to: string | undefined;
	let contentType: ParsedHeaderValue | undefined;
	let date: number | undefined;
	let gmailLabels: string[] | undefined;
    let mimeVersion: string | undefined;

    let warning = false;

	const rawSubjects = parsed['subject'];
	if (rawSubjects) {
		if (rawSubjects.length > 1) {
            warning = true;
			console.log(`Found multiple "Subject" headers: ${rawSubjects.join('; ')}`);
		} 
        if (rawSubjects.length) {
			subject = myUnleak(decodeWords(rawSubjects[0], decodeString).trim());
		}
	}

    const rawFrom = parsed['from'];
	if (rawFrom) {
		if (rawFrom.length > 1) {
            warning = true;
			console.log(`Found multiple "From" headers: ${rawFrom.join('; ')} in email with subject ${subject}`);
		}
        if (rawFrom.length) {
			from = myUnleak(decodeWords(rawFrom[0], decodeString).trim());
		}
	}

    const rawTo = parsed['to'];
	if (rawTo) {
		if (rawTo.length > 1) {
            warning = true;
			console.log(`Found multiple "To" headers: ${rawTo.join('; ')} in email with subject ${subject}`);
		} 
        if (rawTo.length) {
			to = myUnleak(decodeWords(rawTo[0], decodeString).trim());
		}
	}

	const rawContentTypes = parsed['content-type'];
	if (rawContentTypes) {
		if (rawContentTypes.length > 1) {
            warning = true;
			console.log(`Found multiple "Content-Type" headers: ${rawContentTypes.join('; ')} in email with subject ${subject}`);
		}
        if(rawContentTypes.length) {
			contentType = parseHeaderValueAndParams(myUnleak(rawContentTypes[0]), decodeString);
		}
	}

	const rawDates = parsed['date'];
	if (rawDates) {
		if (rawDates.length > 1) {
            warning = true;
			console.log(`Found multiple "Date" headers: ${rawDates.join('; ')} in email with subject ${subject}`);
		} 
        if (rawDates.length) {
			date = getUnixMs(rawDates[0])?.ms;
		}
	}

	const rawGmailLabels = parsed['x-gmail-labels'];
	if (rawGmailLabels) {
		if (rawGmailLabels.length > 1) {
            warning = true;
			console.log(
				`Found multiple "X-Gmail-Labels" headers: ${rawGmailLabels.join('; ')} in email with subject ${subject}`
			);
		} 
        if (rawGmailLabels.length) {
			gmailLabels = decodeWords(myUnleak(rawGmailLabels[0]), decodeString)
				.split(',')
				.map(s => s.trim());
		}
	}

    const rawMimeVersion = parsed['mime-version'];
	if (rawMimeVersion) {
		if (rawMimeVersion.length > 1) {
            warning = true;
			console.log(
				`Found multiple "MIME-Version" headers: ${rawMimeVersion.join('; ')} in email with subject ${subject}`
			);
		} 
        if(rawMimeVersion.length) {
            mimeVersion = decodeWords(myUnleak(rawMimeVersion[0]), decodeString).trim();
		}
	}
	return {
		subject,
        from,
        to,
		contentType: contentType?.value,
		date: date,
		labels: gmailLabels,
        mimeVersion,
        warning
	};
} // parseGmailHeaders()

// This function copied line for line from gflib's cli-utils, which specifically causes issues if we try to import from.
export function decodeStringIconvLite(buf: Buffer, enc?: string): string {
	const enc2 = (enc || 'utf8').trim().toLowerCase();

	// Switch iso-8859-8-i to iso-8859-8
	const enc3 = enc2.replace(/[\s_-]/g, '') === 'iso88598i' ? 'iso-8859-8' : enc2;

	// Try native
	try {
		return decodeString(buf, enc3);
	} catch (err) {
		// stipBOM false to match Buffer.toString()... doesn't seem to work
		// when decoding from UTF-7
		const result = decode(buf, enc3, { stripBOM: false });
		return result;
    }
} // decodeStringIconvLite()

export function encodeStringIconvLite(str: string, enc?: string): Buffer {
	const enc2 = (enc || 'utf8').trim().toLowerCase();

	// Switch iso-8859-8-i to iso-8859-8
	const enc3 = enc2.replace(/[\s_-]/g, '') === 'iso88598i' ? 'iso-8859-8' : enc2;

	// Try native
	try {
		return encodeString(str, enc3);
	} catch (err) {
		// stipBOM false to match Buffer.toString()... doesn't seem to work
		// when decoding from UTF-7
		const result = encode(str, enc3);
		return result;
	}
} // decodeStringIconvLite

export function range(start: number, end: number, step = 1): number[] {
    const array: number[] = [];
    
    if (step === 0) {
        return [start];
    }
    
    if (start < end) {
        for (let i = start; i <= end; i += step) {
            array.push(i);
        }
    } else {
        for (let i = start; i >= end; i += step) {
            array.push(i);
        }
    }
    
    return array;
} // range()

export function getFromLine(mboxData: MboxData, messageIx: number) {
    const start = mboxData.messageData[messageIx].start;
    let prevEnd: number;
    if(messageIx > 0) {
        prevEnd = mboxData.messageData[messageIx-1].end;
    } else {
        prevEnd = 0;
    }

    return mboxData.reader.readBytes(prevEnd, start);
} // getFromLine()

export function dfsMimeContentType(mimeNode: MimeNode, contentType: string): MimeNode|undefined {
    if(!mimeNode) {
        return undefined;
    }
    if(mimeNode.contentType === contentType) {
        return mimeNode;
    }

    let children: MimeNode[]|undefined;
    try {
        children = (mimeNode as MultipartMimeNode).children;
    } catch {;}
    if(children) {
        for(let i = 0; i < children.length; i++) {
            const childRes = dfsMimeContentType(children[i], contentType);
            if(childRes) {
                return childRes;
            }
        }
    }

    return undefined;
} // dfsMimeContentType()

export function dfsFindAllAttachments(mimeNode: MimeNode): MimeNode[] {
    if(!mimeNode) {
        return [];
    }

    const res: MimeNode[] = [];
    if(mimeNode.contentDisposition && mimeNode.contentDisposition === 'attachment') {
        res.push(mimeNode);
    }

    let children: MimeNode[]|undefined;
    try {
        children = (mimeNode as MultipartMimeNode).children;
    } catch {;}
    if(children) {
        for(let i = 0; i < children.length; i++) {
            const childRes = dfsFindAllAttachments(children[i]);
            res.push(...childRes);
        }
    }

    return res;
} // dfsMimeContentType()

export function b64toBlob(b64: string) {
    const buf = Buffer.from(b64, 'base64');
    return new Blob([buf]);
}

export function triggerBlobDownload(blob: Blob, fileName: string) {
    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = fileName; // simulate link click
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
}

export function formatBytes(bytes: number): string {
    const kilobyte = 1024;
    const megabyte = kilobyte * 1024;
    const gigabyte = megabyte * 1024;

    if (bytes >= gigabyte) {
        return `${(bytes / gigabyte).toFixed(1)} GB`;
    } else if (bytes >= megabyte) {
        return `${(bytes / megabyte).toFixed(1)} MB`;
    } else if (bytes >= kilobyte) {
        return `${(bytes / kilobyte).toFixed(1)} KB`;
    } else {
        return `${bytes} bytes`;
    }
} // formatBytes()

export function formatUnixTimestamp(unixTimestamp: number): string {
    const date = new Date(unixTimestamp);
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-UK', options);
} // formatUnixTimestamp()

export const myUnleak = navigator.userAgent.toLowerCase().includes('firefox') ? (s:string)=>s.split('').join('') : (s:string)=>(' ' + s).slice(1);